import type { NostrEvent } from '@nostrify/nostrify';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BookMarked, Download, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getTag, getTags, formatFileSize } from '@/hooks/useMagazines';

interface EbookCardProps {
  ebook: NostrEvent;
}

export function EbookCard({ ebook }: EbookCardProps) {
  const title = getTag(ebook, 'title');
  const image = getTag(ebook, 'image');
  const summary = getTag(ebook, 'summary');
  const url = getTag(ebook, 'url');
  const mime = getTag(ebook, 'm');
  const size = getTag(ebook, 'size');
  const authors = getTags(ebook, 'author');
  const tags = getTags(ebook, 't');

  const isEpub = mime === 'application/epub+zip';
  const ext = isEpub ? 'EPUB' : 'PDF';
  const fileSize = formatFileSize(size);

  return (
    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 group h-full flex flex-col">
      <div className="relative overflow-hidden bg-muted aspect-[2/3] flex-shrink-0">
        {image ? (
          <img
            src={image}
            alt={title ?? 'Ebook'}
            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-secondary/30 to-secondary/10">
            <BookMarked className="h-12 w-12 text-secondary-foreground/40" />
            <span className="text-sm text-muted-foreground text-center px-2 font-medium">{title}</span>
          </div>
        )}
        <div className="absolute top-2 left-2">
          <Badge className="text-xs bg-background/80 text-foreground border">
            {ext}
          </Badge>
        </div>
      </div>

      <CardContent className="pt-3 pb-2 flex-1">
        <h3 className="font-semibold text-sm leading-tight group-hover:text-primary transition-colors">
          {title ?? 'Untitled'}
        </h3>
        {authors.length > 0 && (
          <p className="text-xs text-muted-foreground mt-0.5">{authors.join(', ')}</p>
        )}
        {summary && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{summary}</p>
        )}
        {fileSize && (
          <p className="text-xs text-muted-foreground mt-1">{fileSize}</p>
        )}
      </CardContent>

      {tags.length > 0 && (
        <CardFooter className="pt-0 pb-2 flex-wrap gap-1">
          {tags.slice(0, 3).map((tag, i) => (
            <Badge key={i} variant="secondary" className="text-xs px-1.5 py-0">
              {tag}
            </Badge>
          ))}
        </CardFooter>
      )}

      {url && (
        <div className="px-3 pb-3">
          <Button asChild size="sm" variant="outline" className="w-full gap-2 text-xs">
            <a href={url} target="_blank" rel="noopener noreferrer" download>
              <Download className="h-3 w-3" />
              Download {ext}
              <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
            </a>
          </Button>
        </div>
      )}
    </Card>
  );
}
